<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';

const props = defineProps({
  form: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
  responsibleType: { type: String, default: 'internal' },
  banks: { type: Array, default: () => [] },
  participants: { type: Array, default: () => [] },
  disabled: { type: Boolean, default: false },
});
const emit = defineEmits(['update']);
const { t } = useI18n();
const responsibleOptions = computed(() => {
  if (props.responsibleType === 'bank') return props.banks;
  if (props.responsibleType !== 'seller') return [];

  return props.participants
    .filter(
      participant => (participant.role || participant.party_role) === 'vendedor'
    )
    .map(participant => ({ id: participant.id, name: participant.name }));
});
const responsibleLabel = computed(() =>
  props.responsibleType === 'seller'
    ? t('SALES.DELIVERY.TOPICS.SELLER_RESPONSIBLE')
    : t('SALES.DELIVERY.TOPICS.BANK_RESPONSIBLE')
);
const update = (field, event) => emit('update', field, event.target.value);
</script>

<template>
  <LaisCard
    :title="t('SALES.DELIVERY.TOPICS.TITLE')"
    prefix-icon="i-lucide-list-checks"
  >
    <div class="grid gap-4">
      <label class="flex items-center gap-2 text-sm text-lais-text">
        <input
          type="checkbox"
          :checked="Boolean(form.reforco)"
          class="size-4 accent-lais-primary"
          :disabled="disabled"
          @change="emit('update', 'reforco', $event.target.checked ? '15' : '')"
        />
        {{ t('SALES.DELIVERY.TOPICS.REINFORCEMENT') }}
      </label>
      <LaisField
        v-if="form.reforco"
        :label="t('SALES.DELIVERY.TOPICS.MOMENT')"
        :error="errors.reforco_observacao"
      >
        <select
          :value="form.reforco"
          class="h-10 w-full rounded-lais-sm border border-lais-border bg-white px-3 text-sm"
          :disabled="disabled"
          @change="update('reforco', $event)"
        >
          <option value="15">
            {{ t('SALES.DELIVERY.TOPICS.AFTER_CERTIFICATES') }}
          </option>
          <option value="16">{{ t('SALES.DELIVERY.TOPICS.OTHER') }}</option>
        </select>
      </LaisField>
      <LaisField
        v-if="form.reforco === '16'"
        :label="t('SALES.DELIVERY.TOPICS.OTHER_DESCRIPTION')"
        :error="errors.reforco_observacao"
        required
      >
        <textarea
          :value="form.reforco_observacao"
          rows="3"
          class="w-full rounded-lais-sm border border-lais-border bg-white p-3 text-sm"
          :disabled="disabled"
          @input="update('reforco_observacao', $event)"
        />
      </LaisField>
      <LaisField
        v-if="responsibleOptions.length"
        :label="responsibleLabel"
        :error="errors.id_responsavel_chaves"
        required
      >
        <select
          :value="form.id_responsavel_chaves || '0'"
          class="h-10 w-full rounded-lais-sm border border-lais-border bg-white px-3 text-sm"
          :disabled="disabled"
          @change="update('id_responsavel_chaves', $event)"
        >
          <option value="0">{{ t('SALES.DELIVERY.TOPICS.SELECT') }}</option>
          <option
            v-for="option in responsibleOptions"
            :key="option.id"
            :value="option.id"
          >
            {{ option.name }}
          </option>
        </select>
      </LaisField>
      <p v-else class="text-sm text-lais-text-body">
        {{ t('SALES.DELIVERY.TOPICS.INTERNAL_RESPONSIBLE') }}
      </p>
    </div>
  </LaisCard>
</template>
